import { can, PortalPermissions } from "../../../../../config/permissions";
import React, { ChangeEvent, FocusEvent, useState } from 'react';
import { withFormik, FormikProps } from 'formik';
import { toast } from 'react-toastify';
import { Row } from 'antd';
import Select from 'antd/lib/select';
import * as Yup from 'yup';
import { Captura, ModalidadePhoebus } from '../../types';
import { IReprocessamentoFilterValues, IReprocessamentoFilterProps } from './types';
import {
  Col,
  InputView,
  FieldLabel,
  ErrorText,
  Button,
  ResetButton,
  Form,
  Label,
} from './styles';

const isPixFlow = (values: IReprocessamentoFilterValues): boolean =>
  values.captura === Captura.Phoebus && values.modalidade === ModalidadePhoebus.Pix;

const validationSchema = Yup.object()
  .shape({
    startDate: Yup.string().required('Data inicial é obrigatória'),
    finishDate: Yup.string().required('Data final é obrigatória'),
    merchant: Yup.string().matches(
      /^\d{6}$/,
      'Código do Lojista deve ter exatamente 6 dígitos numéricos',
    ),
    nsu: Yup.string(),
    endToEndId: Yup.string(),
    appPixId: Yup.string(),
  })
  .test('required-filters', 'Informe pelo menos um filtro obrigatório', function (values) {
    const v = values as unknown as IReprocessamentoFilterValues;

    if (isPixFlow(v)) {
      if (!v.merchant && !v.endToEndId && !v.appPixId) {
        const message = 'Informe pelo menos o Cód. Lojista, o Identificador Pix ou o Identificador do App Pix';
        return new Yup.ValidationError([
          this.createError({ path: 'merchant', message }),
          this.createError({ path: 'endToEndId', message }),
          this.createError({ path: 'appPixId', message }),
        ]);
      }
      return true;
    }

    if (!v.merchant && !v.nsu) {
      const message = 'Informe pelo menos o Código do Lojista ou o NSU';
      return new Yup.ValidationError([
        this.createError({ path: 'merchant', message }),
        this.createError({ path: 'nsu', message }),
      ]);
    }
    return true;
  });

type FormProps = IReprocessamentoFilterProps & FormikProps<IReprocessamentoFilterValues>;

const FormFilter: React.FC<FormProps> = (props) => {
  const {
    handleSubmit,
    resetForm,
    values,
    errors,
    touched,
    setFieldValue,
    setFieldTouched,
    onCapturaModalidadeChange,
  } = props;

  const [startDateType, setStartDateType] = useState<string>('text');
  const [finishDateType, setFinishDateType] = useState<string>('text');

  const pixFlow = isPixFlow(values);
  const isPhoebus = values.captura === Captura.Phoebus;

  const handleFocusStartDate = (event: FocusEvent<HTMLInputElement>): void => {
    setStartDateType('datetime-local');
    if (!event.target.value) {
      const now = new Date();
      const y = now.getFullYear();
      const m = String(now.getMonth() + 1).padStart(2, '0');
      const d = String(now.getDate()).padStart(2, '0');
      const defaultVal = `${y}-${m}-${d}T00:00:00`;
      event.target.value = defaultVal;
      setFieldValue('startDate', defaultVal);
    }
  };

  const handleFocusFinishDate = (event: FocusEvent<HTMLInputElement>): void => {
    setFinishDateType('datetime-local');
    if (!event.target.value) {
      const now = new Date();
      const y = now.getFullYear();
      const m = String(now.getMonth() + 1).padStart(2, '0');
      const d = String(now.getDate()).padStart(2, '0');
      const defaultVal = `${y}-${m}-${d}T23:59:59`;
      event.target.value = defaultVal;
      setFieldValue('finishDate', defaultVal);
    }
  };

  const handleChangeStartDate = (event: ChangeEvent<HTMLInputElement>): void => {
    setFieldValue('startDate', event.target.value);
  };

  const handleChangeFinishDate = (event: ChangeEvent<HTMLInputElement>): void => {
    setFieldValue('finishDate', event.target.value);
  };

  const handleChangeMerchant = (event: ChangeEvent<HTMLInputElement>): void => {
    const onlyDigits = event.target.value.replace(/\D/g, '').slice(0, 6);
    setFieldValue('merchant', onlyDigits);
  };

  const handleChangeNsu = (event: ChangeEvent<HTMLInputElement>): void => {
    setFieldValue('nsu', event.target.value);
  };

  const handleChangeEndToEndId = (event: ChangeEvent<HTMLInputElement>): void => {
    setFieldValue('endToEndId', event.target.value);
  };

  const handleChangeAppPixId = (event: ChangeEvent<HTMLInputElement>): void => {
    setFieldValue('appPixId', event.target.value);
  };

  const handleChangeCaptura = (value: Captura): void => {
    setFieldValue('captura', value);
    onCapturaModalidadeChange?.(value, values.modalidade);
  };

  const handleChangeModalidade = (value: ModalidadePhoebus): void => {
    setFieldValue('modalidade', value);
    onCapturaModalidadeChange?.(values.captura, value);
  };

  const handleReset = async () => {
    setStartDateType('text');
    setFinishDateType('text');
    await resetForm();
  };

  const handleSubmitWithToasts = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!values.startDate) toast.error('Data início é obrigatória.');
    if (!values.finishDate) toast.error('Data fim é obrigatória.');

    if (pixFlow) {
      if (!values.merchant && !values.endToEndId && !values.appPixId) {
        toast.error('Informe pelo menos o Cód. Lojista, o Identificador Pix ou o Identificador do App Pix.');
      } else if (values.merchant && !/^\d{6}$/.test(values.merchant)) {
        toast.error('Cód. Lojista deve ter exatamente 6 dígitos numéricos.');
      }
    } else if (!values.merchant && !values.nsu) {
      toast.error('Informe pelo menos o Cód. Lojista ou o NSU.');
    } else if (values.merchant && !/^\d{6}$/.test(values.merchant)) {
      toast.error('Cód. Lojista deve ter exatamente 6 dígitos numéricos.');
    }

    handleSubmit();
  };

  const colBreakpoints = {
    xs: { flex: '100%' },
    sm: { flex: '50%' },
    md: { flex: '40%' },
    lg: { flex: '20%' },
    xl: { flex: '10%' },
  };

  return (
    <Form onSubmit={handleSubmitWithToasts}>
      <Row>
        <Label>Filtros:</Label>
      </Row>
      <Row gutter={16}>
        <Col {...colBreakpoints}>
          <InputView>
            <FieldLabel>Captura *</FieldLabel>
            <Select
              value={values.captura}
              style={{ width: '100%' }}
              dropdownMatchSelectWidth={false}
              onChange={handleChangeCaptura}
              options={[
                { value: Captura.Aditum, label: 'Aditum' },
                { value: Captura.Phoebus, label: 'Phoebus' },
              ]}
            />
          </InputView>
        </Col>

        {isPhoebus && (
          <Col {...colBreakpoints}>
            <InputView>
              <FieldLabel>Modalidade *</FieldLabel>
              <Select
                value={values.modalidade}
                style={{ width: '100%' }}
                dropdownMatchSelectWidth={false}
                onChange={handleChangeModalidade}
                options={[
                  { value: ModalidadePhoebus.DebitoCredito, label: 'Débito/Crédito' },
                  { value: ModalidadePhoebus.Pix, label: 'Pix' },
                ]}
              />
            </InputView>
          </Col>
        )}

        <Col {...colBreakpoints}>
          <InputView>
            <FieldLabel>Data inicial *</FieldLabel>
            <input
              type={startDateType}
              name="startDate"
              placeholder="Data inicial"
              step="1"
              value={values.startDate}
              onFocus={handleFocusStartDate}
              onChange={handleChangeStartDate}
              onBlur={() => setFieldTouched('startDate', true)}
            />
            {touched.startDate && errors.startDate && (
              <ErrorText>{errors.startDate}</ErrorText>
            )}
          </InputView>
        </Col>

        <Col {...colBreakpoints}>
          <InputView>
            <FieldLabel>Data final *</FieldLabel>
            <input
              type={finishDateType}
              name="finishDate"
              placeholder="Data final"
              step="1"
              value={values.finishDate}
              onFocus={handleFocusFinishDate}
              onChange={handleChangeFinishDate}
              onBlur={() => setFieldTouched('finishDate', true)}
            />
            {touched.finishDate && errors.finishDate && (
              <ErrorText>{errors.finishDate}</ErrorText>
            )}
          </InputView>
        </Col>

        <Col {...colBreakpoints}>
          <InputView>
            <FieldLabel>Cod. Lojista</FieldLabel>
            <input
              type="text"
              name="merchant"
              placeholder="Cod. Lojista"
              inputMode="numeric"
              maxLength={6}
              value={values.merchant}
              onChange={handleChangeMerchant}
              onBlur={() => setFieldTouched('merchant', true)}
            />
            {touched.merchant && errors.merchant && (
              <ErrorText>{errors.merchant}</ErrorText>
            )}
          </InputView>
        </Col>

        {!pixFlow && (
          <Col {...colBreakpoints}>
            <InputView>
              <FieldLabel>NSU</FieldLabel>
              <input
                type="text"
                name="nsu"
                placeholder="NSU"
                value={values.nsu}
                onChange={handleChangeNsu}
                onBlur={() => setFieldTouched('nsu', true)}
              />
              {(touched.merchant || touched.nsu) && errors.nsu && (
                <ErrorText>{errors.nsu}</ErrorText>
              )}
            </InputView>
          </Col>
        )}

        {pixFlow && (
          <>
            <Col {...colBreakpoints}>
              <InputView>
                <FieldLabel>Identificador Pix</FieldLabel>
                <input
                  type="text"
                  name="endToEndId"
                  placeholder="Identificador Pix"
                  value={values.endToEndId}
                  onChange={handleChangeEndToEndId}
                  onBlur={() => setFieldTouched('endToEndId', true)}
                />
                {(touched.merchant || touched.endToEndId || touched.appPixId) && errors.endToEndId && (
                  <ErrorText>{errors.endToEndId}</ErrorText>
                )}
              </InputView>
            </Col>

            <Col {...colBreakpoints}>
              <InputView>
                <FieldLabel>Identificador do App Pix</FieldLabel>
                <input
                  type="text"
                  name="appPixId"
                  placeholder="Identificador do App Pix"
                  value={values.appPixId}
                  onChange={handleChangeAppPixId}
                  onBlur={() => setFieldTouched('appPixId', true)}
                />
                {(touched.merchant || touched.endToEndId || touched.appPixId) && errors.appPixId && (
                  <ErrorText>{errors.appPixId}</ErrorText>
                )}
              </InputView>
            </Col>
          </>
        )}

        <Col {...colBreakpoints}>
          <ResetButton onClick={handleReset}>
            Limpar filtros
          </ResetButton>
        </Col>
        <Col {...colBreakpoints}>
          {can(PortalPermissions.transacoes.reprocessamento.reprocessar) && (
            <Button htmlType="submit">
              Reprocessar
            </Button>
          )}
        </Col>
      </Row>
    </Form>
  );
};

export default withFormik<IReprocessamentoFilterProps, IReprocessamentoFilterValues>({
  mapPropsToValues: (): IReprocessamentoFilterValues => ({
    captura: Captura.Phoebus,
    modalidade: ModalidadePhoebus.DebitoCredito,
    startDate: '',
    finishDate: '',
    merchant: '',
    nsu: '',
    endToEndId: '',
    appPixId: '',
  }),
  validationSchema,
  handleSubmit: (values, { props }) => {
    props.onFormSubmit(values);
  },
})(FormFilter);
